import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type InvestmentsFormType, getInvestmentsFormSchema } from "Forms/Schemas/GoalsFulfillment/CurrentSolution/investmentsFormSchema";
import {
  getGetAssetAllocationOverviewQueryKey,
  getListCurrentContractsQueryKey,
  useCreateCurrentContract,
  usePatchCurrentContract,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  CreateCurrentContractInvestmentBodyInvestmentToolType,
  type CreateCurrentContractInvestmentBodyProposedStepActionType,
  type CreateCurrentContractInvestmentBodySolutionHorizonType,
  type CurrentContractBaseInvestmentToolType,
  type CurrentContractBaseSolutionHorizonType,
  type CurrentContractDetailInvestment,
  CurrentContractListItemProposedStepActionType,
  type CurrentSolutionAssetOption,
  PatchCurrentContractInvestmentBodyInvestmentToolType,
  type PatchCurrentContractInvestmentBodyProposedStepActionType,
} from "Infrastructure/ApiZfp/Schema";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useGoalsFulfillmentContext } from "../../GoalsFulfillmentContext";
import { CharacteristicsAccordion } from "../CharacteristicsAccordion";
import type { CurrentSolutionFormHandle } from "./CurrentSolutionWrapperForm";
import { ModifyDepositFields } from "./ModifyDepositFields";

type Props = {
  readonly edit?: boolean;
  readonly investmentToolType: CurrentContractBaseInvestmentToolType;
  readonly institution?: string;
  readonly horizonType: CurrentContractBaseSolutionHorizonType;
  readonly onSuccess?: () => void;
  readonly currentSolutionId?: string;
  readonly initialData?: CurrentContractDetailInvestment | null;
};

export const InvestmentsForm = forwardRef<CurrentSolutionFormHandle, Props>(
  ({ investmentToolType, institution, edit, horizonType, onSuccess, currentSolutionId, initialData }, ref) => {
    const { t } = useResource();
    const queryClient = useQueryClient();
    const institutionRef = useRef(institution);

    const { analysisId, versionId } = useContext(AnalysisDetailContext);
    const { assetAllocationOverview } = useGoalsFulfillmentContext();

    const { mutate: createMutation } = useCreateCurrentContract();
    const { mutate: updateMutation } = usePatchCurrentContract();

    const proposedStepActionOptions: Option[] = (
      [
        CurrentContractListItemProposedStepActionType.Keep,
        CurrentContractListItemProposedStepActionType.Modify,
        CurrentContractListItemProposedStepActionType.Cancel,
      ] as Array<keyof typeof CurrentContractListItemProposedStepActionType>
    ).map((value) => ({
      value,
      name: t(Resources.AnalysisDetail.GoalsFulfillment.ProposedStepActionTypeOptions[value]),
    }));

    const methods = useForm<InvestmentsFormType>({
      resolver: yupResolver(getInvestmentsFormSchema(t, assetAllocationOverview?.isPortfolioAssetAllocationRequired)),
      mode: "onChange",
      defaultValues:
        edit && initialData
          ? {
              investmentToolType: initialData.investmentToolType as any,
              institution: initialData.partnerName ?? "",
              currentValue: initialData.oneTimeAmount?.value ?? 0,
              regularAmount: initialData.regularAmount?.value ?? 0,
              horizon: initialData.currentSolutionHorizon ?? 0,
              appreciationRate: initialData.appreciationRate ?? 0,
              equities: initialData.assetAllocation?.equities ?? 0,
              bonds: initialData.assetAllocation?.bonds ?? 0,
              commodities: initialData.assetAllocation?.commodities ?? 0,
              realEstate: initialData.assetAllocation?.realEstate ?? 0,
              cash: initialData.assetAllocation?.cash ?? 0,
              other: initialData.assetAllocation?.others ?? 0,
              entryFee: initialData.entryFee ?? 0,
              oneTimeFee: initialData.oneTimeFee?.value ?? 0,
              managementFee: initialData.managementFee ?? 0,
              exitFee: initialData.exitFee ?? 0,
              proposedStepAction: initialData.proposedStepActionType,
              newDeposit: (initialData.oneTimeAmount?.value ?? 0) + (initialData.oneTimeAmountChange?.value ?? 0),
              newRegularDeposit: (initialData.regularAmount?.value ?? 0) + (initialData.regularAmountChange?.value ?? 0),
              proposedSolutionHorizon: initialData.proposedSolutionHorizon ?? 0,
              proposedSolutionEntryFee: initialData.proposedSolutionEntryFee ?? 0,
              proposedSolutionManagementFee: initialData.proposedSolutionManagementFee ?? 0,
              note: initialData.note ?? "",
            }
          : {
              investmentToolType,
              institution,
            },
    });

    const {
      control,
      watch,
      formState: { errors },
      handleSubmit,
      reset,
      setValue,
      getValues,
    } = methods;

    useEffect(() => {
      if (edit && initialData) {
        reset({
          investmentToolType: initialData.investmentToolType as any,
          institution: institutionRef.current,
          currentValue: initialData.oneTimeAmount?.value ?? 0,
          regularAmount: initialData.regularAmount?.value ?? 0,
          horizon: initialData.currentSolutionHorizon ?? 0,
          appreciationRate: initialData.appreciationRate ?? 0,
          equities: initialData.assetAllocation?.equities ?? 0,
          bonds: initialData.assetAllocation?.bonds ?? 0,
          commodities: initialData.assetAllocation?.commodities ?? 0,
          realEstate: initialData.assetAllocation?.realEstate ?? 0,
          cash: initialData.assetAllocation?.cash ?? 0,
          other: initialData.assetAllocation?.others ?? 0,
          entryFee: initialData.entryFee ?? 0,
          oneTimeFee: initialData.oneTimeFee?.value ?? 0,
          managementFee: initialData.managementFee ?? 0,
          exitFee: initialData.exitFee ?? 0,
          proposedStepAction: initialData.proposedStepActionType,
          newDeposit: (initialData.oneTimeAmount?.value ?? 0) + (initialData.oneTimeAmountChange?.value ?? 0),
          newRegularDeposit: (initialData.regularAmount?.value ?? 0) + (initialData.regularAmountChange?.value ?? 0),
          proposedSolutionHorizon: initialData.proposedSolutionHorizon ?? 0,
          proposedSolutionEntryFee: initialData.proposedSolutionEntryFee ?? 0,
          proposedSolutionManagementFee: initialData.proposedSolutionManagementFee ?? 0,
          note: initialData.note ?? "",
        });
      }
    }, [edit, initialData, reset]);

    const proposedStepAction = watch("proposedStepAction");

    useEffect(() => {
      setValue("institution", institution ?? "", { shouldValidate: true });
    }, [institution, setValue]);

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (edit) {
          handleSubmit(onEdit)();
        } else {
          handleSubmit(onCreate)();
        }
      },
      prefillFromAssetOption: (asset: CurrentSolutionAssetOption) => {
        setValue("investmentToolType", asset.investmentToolType as any, { shouldValidate: true, shouldDirty: true });
        setValue("currentValue", asset.amount?.value ?? 0, { shouldValidate: true, shouldDirty: true });
        setValue("regularAmount", asset.regularAmount?.value ?? 0, { shouldValidate: true, shouldDirty: true });
      },
    }));

    const onCreate = (data: InvestmentsFormType) => {
      const isProposedStepModify = data.proposedStepAction === CurrentContractListItemProposedStepActionType.Modify;

      createMutation(
        {
          analysisId,
          versionId,
          data: {
            investmentToolType: CreateCurrentContractInvestmentBodyInvestmentToolType.investment,
            solutionHorizonType: horizonType as CreateCurrentContractInvestmentBodySolutionHorizonType,
            proposedStepActionType: data.proposedStepAction as CreateCurrentContractInvestmentBodyProposedStepActionType,
            partnerName: institution ?? "",
            oneTimeAmount: {
              value: data.currentValue,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            regularAmount: {
              value: data.regularAmount,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.monthly,
            },
            oneTimeAmountChange: isProposedStepModify
              ? {
                  value: (data.newDeposit ?? 0) - data.currentValue,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.one_time,
                }
              : undefined,
            regularAmountChange: isProposedStepModify
              ? {
                  value: (data.newRegularDeposit ?? 0) - data.regularAmount,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.monthly,
                }
              : undefined,
            assetAllocation: {
              equities: data.equities ?? 0,
              bonds: data.bonds ?? 0,
              commodities: data.commodities ?? 0,
              realEstate: data.realEstate ?? 0,
              cash: data.cash ?? 0,
              others: data.other ?? 0,
            },
            currentSolutionHorizon: data.horizon ?? 0,
            appreciationRate: data.appreciationRate,
            note: data.note ?? "",
            oneTimeFee: {
              value: data.oneTimeFee ?? 0,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            exitFee: data.exitFee ?? 0,
            entryFee: data.entryFee ?? 0,
            managementFee: data.managementFee ?? 0,
            proposedSolutionHorizon: isProposedStepModify ? (data.proposedSolutionHorizon ?? null) : null,
            proposedSolutionEntryFee: isProposedStepModify ? (data.proposedSolutionEntryFee ?? null) : null,
            proposedSolutionManagementFee: isProposedStepModify ? (data.proposedSolutionManagementFee ?? null) : null,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListCurrentContractsQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
            onSuccess?.();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
          },
        },
      );
    };

    const onEdit = (data: InvestmentsFormType) => {
      if (!currentSolutionId) return;
      const isProposedStepModify = data.proposedStepAction === CurrentContractListItemProposedStepActionType.Modify;
      updateMutation(
        {
          analysisId,
          versionId,
          currentContractGuid: currentSolutionId,
          data: {
            investmentToolType: PatchCurrentContractInvestmentBodyInvestmentToolType.investment,
            solutionHorizonType: horizonType,
            partnerName: institution ?? "",
            proposedStepActionType: data.proposedStepAction as PatchCurrentContractInvestmentBodyProposedStepActionType,
            oneTimeAmount: {
              value: data.currentValue,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            regularAmount: {
              value: data.regularAmount,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.monthly,
            },
            oneTimeAmountChange: isProposedStepModify
              ? {
                  value: (data.newDeposit ?? 0) - data.currentValue,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.one_time,
                }
              : undefined,
            regularAmountChange: isProposedStepModify
              ? {
                  value: (data.newRegularDeposit ?? 0) - data.regularAmount,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.monthly,
                }
              : undefined,
            assetAllocation: {
              equities: data.equities ?? 0,
              bonds: data.bonds ?? 0,
              commodities: data.commodities ?? 0,
              realEstate: data.realEstate ?? 0,
              cash: data.cash ?? 0,
              others: data.other ?? 0,
            },
            currentSolutionHorizon: data.horizon ?? 0,
            appreciationRate: data.appreciationRate,
            note: data.note ?? "",
            oneTimeFee: {
              value: data.oneTimeFee ?? 0,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            exitFee: data.exitFee ?? 0,
            entryFee: data.entryFee ?? 0,
            managementFee: data.managementFee ?? 0,
            proposedSolutionHorizon: isProposedStepModify ? (data.proposedSolutionHorizon ?? null) : null,
            proposedSolutionEntryFee: isProposedStepModify ? (data.proposedSolutionEntryFee ?? null) : null,
            proposedSolutionManagementFee: isProposedStepModify ? (data.proposedSolutionManagementFee ?? null) : null,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListCurrentContractsQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
            onSuccess?.();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
          },
        },
      );
    };

    const handleProposedStepActionChange = (value: string) => {
      if (value === CurrentContractListItemProposedStepActionType.Modify) {
        const currentForm = getValues();
        const initializationMap: Record<string, any> = {
          newDeposit: currentForm.currentValue,
          newRegularDeposit: currentForm.regularAmount,
          proposedSolutionHorizon: currentForm.horizon,
          proposedSolutionEntryFee: currentForm.entryFee,
          proposedSolutionManagementFee: currentForm.managementFee,
        };

        for (const [field, sourceValue] of Object.entries(initializationMap)) {
          const currentValue = getValues(field as any);
          if (currentValue === undefined || currentValue === null || currentValue === "" || currentValue === 0) {
            setValue(field as any, sourceValue, {
              shouldDirty: true,
            });
          }
        }
      }
    };

    return (
      <FormProvider {...methods}>
        <form>
          <Stack spacing={Spacing.XXL}>
            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    name="currentValue"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.CurrentValue)}
                    errors={errors}
                    labelEndAdornment={<StyledLabelAdornment />}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}
                      </Typography>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    name="regularAmount"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RegularAmount)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                    inputEndAdornment={
                      <Box sx={{ width: 100 }}>
                        <Typography variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>

                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    name="horizon"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Horizon)}
                    errors={errors}
                    labelEndAdornment={<StyledLabelAdornment />}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.Years)}
                      </Typography>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                      },
                    ]}
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    name="appreciationRate"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.AppreciationRate)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                  />
                </Grid>

                <Stack direction="column">
                  <Box sx={{ width: "100%" }}>
                    <Typography variant="body2" color="secondary" fontWeight={500}>
                      {t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Fees)}
                    </Typography>
                  </Box>

                  <Grid container spacing={Spacing.XL}>
                    <Grid size={3}>
                      <BlFormInput
                        control={control}
                        name="entryFee"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.EntryFeeShort)}
                        errors={errors}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            %
                          </Typography>
                        }
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 2,
                          },
                        ]}
                      />
                    </Grid>
                    <Grid size={3}>
                      <BlFormInput
                        control={control}
                        name="oneTimeFee"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.OneTimeFeeShort)}
                        errors={errors}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            {t(Resources.Common.CZKSuffix)}
                          </Typography>
                        }
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 2,
                          },
                        ]}
                      />
                    </Grid>
                    <Grid size={3}>
                      <BlFormInput
                        control={control}
                        name="managementFee"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ManagementFeeShort)}
                        errors={errors}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            %
                          </Typography>
                        }
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 2,
                          },
                        ]}
                      />
                    </Grid>
                    <Grid size={3}>
                      <BlFormInput
                        control={control}
                        name="exitFee"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ExitFeeShort)}
                        errors={errors}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            %
                          </Typography>
                        }
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 2,
                          },
                        ]}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>
            </Box>

            <Box>
              <CharacteristicsAccordion<InvestmentsFormType>
                key={assetAllocationOverview?.isPortfolioAssetAllocationRequired ? "open" : "closed"}
                defaultExpanded={assetAllocationOverview?.isPortfolioAssetAllocationRequired}
              />
            </Box>

            <Divider />

            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t(Resources.AnalysisDetail.GoalsFulfillment.ProposedSolutionSteps)}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <FormDropdown
                    name="proposedStepAction"
                    options={proposedStepActionOptions}
                    control={control}
                    labelEndAdornment={<StyledLabelAdornment />}
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.ProposedStepActionTypeOptions.Title)}
                    errors={errors}
                    onChange={handleProposedStepActionChange}
                  />
                </Grid>

                {proposedStepAction === CurrentContractListItemProposedStepActionType.Modify && (
                  <>
                    <ModifyDepositFields />
                    <Grid size={4}>
                      <BlFormInput
                        control={control}
                        name="proposedSolutionHorizon"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ProposedSolutionHorizon)}
                        errors={errors}
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 0,
                          },
                        ]}
                        labelEndAdornment={<StyledLabelAdornment />}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            {t(Resources.Common.Years)}
                          </Typography>
                        }
                      />
                    </Grid>

                    <Grid size={4}>
                      <BlFormInput
                        control={control}
                        name="proposedSolutionEntryFee"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ProposedSolutionEntryFee)}
                        errors={errors}
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 2,
                          },
                        ]}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            %
                          </Typography>
                        }
                      />
                    </Grid>

                    <Grid size={4}>
                      <BlFormInput
                        control={control}
                        name="proposedSolutionManagementFee"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ProposedSolutionManagementFee)}
                        errors={errors}
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 2,
                          },
                        ]}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            %
                          </Typography>
                        }
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <BlFormInput
                    control={control}
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Note)}
                    placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.NotePlaceholder)}
                    name="note"
                    errors={errors}
                    multiline
                    minRows={6}
                    maxRows={8}
                    characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </form>
      </FormProvider>
    );
  },
);
